import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { Form, FormikProvider, FormikValues, useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { gridSpacing } from 'store/constant';
import { DateRange } from 'types';
import ColorPalette from './ColorPalette';

import DateRangeIcon from '@mui/icons-material/DateRange';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RootState } from 'app/store';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { fetchParticipantsList } from 'slices/HomeTilesSlices';
import {
  fetchClientStaff,
  getServicesList
} from 'slices/NdisSlices/calenderEvents/CalenderEventsSlice';

// Define the type for the selected client
interface Client {
  label: string;
  id: number;
  email: string;
}

interface AddEventFormProps {
  event: FormikValues | null;
  range: DateRange | null;
  handleDelete: (eventId: string, managerEmail: string, supportWorkerEmail: string) => void;
  handleCreate: (d: FormikValues) => void;
  handleUpdate: (i: string, d: FormikValues) => void;
  onCancel: () => void;
}

const AddEventForm = ({
  event,
  range,
  handleDelete,
  handleCreate,
  handleUpdate,
  onCancel
}: AddEventFormProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isCreating = !event;

  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector(
    (state) => state.serviceSelected.selectedServiceTitle
  );

  const servicesList = useAppSelector((state) => state.calendarEvents.servicesList);
  const staffList = useAppSelector((state) => state.calendarEvents.staffList || {});
  const managersList = staffList?.Manager || [];
  const supportWorkers = staffList?.['Support worker'] || [];

  const { pathname } = useLocation();
  const componentParentId = pathname;

  const userDetailsString = localStorage.getItem('user-details');
  const userDetails = JSON.parse(userDetailsString || '{}');
  const selectedClientString = localStorage.getItem('selectedClient');
  const selectedClient = JSON.parse(selectedClientString || '{}');

  const [isClientsLoading, setIsClientsLoading] = useState<boolean>(false);
  const [clientsError, setClientsError] = useState<string | null>(null);

  const isAdminLogin =
    JSON.parse(localStorage.getItem('selectedProject') || '{}')?.isAdmin === true &&
    userDetails?.role?.some((r: any) => r.label.toLowerCase() === 'admin');

  // Fetch clients
  useEffect(() => {
    if (
      selectedProjectId &&
      selectedServiceTitle &&
      userDetails?.email &&
      userDetails?.role?.length > 0
    ) {
      setIsClientsLoading(true);
      dispatch(
        fetchParticipantsList({
          group_id: selectedProjectId || '',
          service_name_id: selectedServiceTitle || '',
          nested_component_id: pathname,
          email: isAdminLogin ? '' : userDetails.email,
          roles: userDetails.role.map((r: any) => r.label),
          client_name: ''
        })
      )
        .unwrap()
        .then(() => {
          setIsClientsLoading(false);
        })
        .catch((error) => {
          setIsClientsLoading(false);
          setClientsError('Failed to load clients. Please try again later.');
          console.error('Failed to fetch participants list:', error);
        });
    } else {
      setIsClientsLoading(false);
    }
  }, [dispatch, selectedProjectId, selectedServiceTitle]);

  const participantsList = useSelector(
    (state: RootState) => state?.homeTilesServices?.participants
  ) || { participants_list: [] };

  const clientsList = useMemo(
    () =>
      participantsList.participants_list.map(
        (participant: { name: string; email: string }, index: number) => ({
          label: participant.name,
          id: index + 1,
          email: participant.email
        })
      ),
    [participantsList.participants_list]
  );

  const getInitialValues = (event: FormikValues | null, range: DateRange | null) => {
    const newEvent = {
      loggedIN_user_id: userDetails?.name || '',
      loggedIN_user_email: userDetails?.email || '',
      group_id: selectedProjectId || '',
      service_name_id: selectedServiceTitle || '',
      component_parent_id: componentParentId || '',
      nested_component_id: 'weekly-patients-schedule',
      user_whoRequested_id: selectedClient?.label || '',
      event_id: '',
      id: '',
      title: '',
      description: '',
      color: '#2196f3',
      textColor: '',
      allDay: false,
      start: range ? new Date(range.start) : new Date(),
      end: range ? new Date(range.end) : new Date(),
      support_item_number: '',
      support_worder: '',
      manager: '',
      client_name: selectedClient?.label || '',
      managers_email: '',
      supportworkers_email: ''
    };

    if (event) {
      return {
        ...newEvent,
        ...event,
        manager: event.manager_name || event.manager || '',
        support_worder: event.support_worker || event.support_worder || '',
        client_name: event.client_name || '',
        support_item_number: event.support_item_number || '',
        managers_email: event.managers_email || '',
        supportworkers_email: event.supportworkers_email || '',
        start: event.start ? new Date(event.start) : newEvent.start,
        end: event.end ? new Date(event.end) : newEvent.end
      };
    }

    return newEvent;
  };

  const EventSchema = Yup.object().shape({
    title: Yup.string().max(255).required('Title is required'),
    description: Yup.string().max(5000),
    end: Yup.date().when(
      'start',
      (start, schema) => start && schema.min(start, 'End date must be later than start date')
    ),
    start: Yup.date(),
    color: Yup.string().max(255),
    textColor: Yup.string().max(255),
    client_name: Yup.string().required('Client name is required'),
    manager: Yup.string().required('Manager is required'),
    support_worder: Yup.string().required('Support worker is required'),
    support_item_number: Yup.string().required('Support item number is required')
  });

  const formik = useFormik({
    initialValues: getInitialValues(event, range),
    validationSchema: EventSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        const selectedManager = managersList.find((m) => m.name === values.manager);
        const selectedSupportWorker = supportWorkers.find(
          (sw) => sw.name === values.support_worder
        );
        const data = {
          loggedIN_user_id: values.loggedIN_user_id,
          loggedIN_user_email: values.loggedIN_user_email,
          group_id: values.group_id,
          service_name_id: values.service_name_id,
          component_parent_id: values.component_parent_id,
          nested_component_id: values.nested_component_id,
          user_whoRequested_id: values.user_whoRequested_id,
          event_id: values.id,
          title: values.title,
          description: values.description,
          color: values.color,
          textColor: values.textColor,
          allDay: values.allDay,
          start: values.start,
          end: values.end,
          id: values.id,
          support_item_number: values.support_item_number,
          support_worder: values.support_worder,
          manager: values.manager,
          client_name: values.client_name,
          managers_email: selectedManager?.email || '',
          supportworkers_email: selectedSupportWorker?.email || '',
          actual_start: '',
          actual_end: '',
          actual_worked_hours: '',
          creation_date_time: '',
          created_by: ''
        };

        if (event) {
          handleUpdate(event.id, data);
        } else {
          handleCreate(data);
        }

        resetForm();
        onCancel();
        setSubmitting(false);
      } catch (error) {
        console.error('Form Submission Error:', error);
      }
    }
  });

  const { values, errors, touched, handleSubmit, isSubmitting, getFieldProps, setFieldValue } =
    formik;

  // Fetch services and staff when client changes
  useEffect(() => {
    if (values.client_name && selectedProjectId && selectedServiceTitle && userDetails?.email) {
      const selectedClient = clientsList.find((client) => client.label === values.client_name);
      if (selectedClient) {
        // Fetch services
        dispatch(getServicesList(selectedClient.email));

        // Fetch staff
        const approvedUser = userDetails.email.split('@')[0].replace('.', '_');
        dispatch(
          fetchClientStaff({
            group_id: selectedProjectId,
            service_name_id: selectedServiceTitle.replace(' ', '_'),
            nested_component_id: 'Staff_List',
            email_loggedIn_userEmail: userDetails.email,
            roles: [userDetails?.role],
            client_name: selectedClient.label.replace(' ', '_'),
            approved_user: approvedUser,
            curr_loggIn_userRoles: userDetails?.name || ''
          })
        );
      }
    }
  }, [dispatch, values.client_name, selectedProjectId, selectedServiceTitle]);

  const backgroundColor = [
    { value: theme.palette.primary.main, color: 'primary.main', label: 'Default' },
    { value: theme.palette.error.main, color: 'error.main' },
    { value: theme.palette.success.dark, color: 'success.dark' },
    { value: theme.palette.secondary.main, color: 'secondary.main' },
    { value: theme.palette.warning.dark, color: 'warning.dark' },
    { value: theme.palette.orange.dark, color: 'orange.dark' },
    { value: theme.palette.grey[500], color: 'grey.500' },
    { value: theme.palette.primary.light, color: 'primary.light' },
    { value: theme.palette.error.light, color: 'error.light' },
    { value: theme.palette.success.light, color: 'success.light' },
    { value: theme.palette.secondary.light, color: 'secondary.light' },
    { value: theme.palette.warning.light, color: 'warning.light' },
    { value: theme.palette.orange.light, color: 'orange.light' }
  ];

  const textColor = [
    { value: theme.palette.error.light, color: 'error.light', label: '' },
    { value: theme.palette.success.light, color: 'success.light', label: '' },
    { value: theme.palette.secondary.light, color: 'secondary.light', label: '' },
    { value: theme.palette.warning.light, color: 'warning.light', label: '' },
    { value: theme.palette.orange.light, color: 'orange.light', label: '' },
    { value: theme.palette.primary.light, color: 'primary.light', label: '' },
    { value: theme.palette.primary.main, color: 'primary.main', label: '' },
    { value: theme.palette.error.main, color: 'error.main', label: '' },
    { value: theme.palette.success.dark, color: 'success.dark', label: '' },
    { value: theme.palette.secondary.main, color: 'secondary.main', label: '' },
    { value: theme.palette.warning.dark, color: 'warning.dark', label: '' },
    { value: theme.palette.orange.dark, color: 'orange.dark', label: '' },
    { value: theme.palette.grey[500], color: 'grey.500', label: '' }
  ];

  return (
    <FormikProvider value={formik}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle>{event ? 'Edit Event' : 'Add Event'}</DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 3 }}>
            {clientsError && (
              <Typography color="error" sx={{ mb: 2 }}>
                {clientsError}
              </Typography>
            )}
            <Grid container spacing={gridSpacing}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  {...getFieldProps('title')}
                  error={Boolean(touched.title && errors.title)}
                  helperText={touched.title && errors.title}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  {...getFieldProps('description')}
                  error={Boolean(touched.description && errors.description)}
                  helperText={touched.description && errors.description}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={Boolean(touched.client_name && errors.client_name)}>
                  <InputLabel>Client Name</InputLabel>
                  <Select
                    label="Client Name"
                    {...getFieldProps('client_name')}
                    value={values.client_name || ''}
                    onChange={(e) => {
                      setFieldValue('client_name', e.target.value);
                      setFieldValue('manager', '');
                      setFieldValue('support_worder', '');
                      setFieldValue('support_item_number', '');
                      setFieldValue('managers_email', '');
                      setFieldValue('supportworkers_email', '');
                    }}
                  >
                    {clientsList.map((item) => (
                      <MenuItem key={item.id} value={item.label}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.client_name && errors.client_name && (
                    <FormHelperText error>{errors.client_name as string}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={Boolean(touched.manager && errors.manager)}>
                  <InputLabel>Manager</InputLabel>
                  <Select
                    label="Manager"
                    {...getFieldProps('manager')}
                    value={values.manager || ''}
                    onChange={(e) => {
                      const selectedManager = managersList.find((m) => m.name === e.target.value);
                      setFieldValue('manager', e.target.value);
                      setFieldValue('managers_email', selectedManager?.email || '');
                    }}
                  >
                    {managersList.map((item: { id: string; name: string }) => (
                      <MenuItem key={item.id} value={item.name}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.manager && errors.manager && (
                    <FormHelperText error>{errors.manager as string}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  error={Boolean(touched.support_worder && errors.support_worder)}
                >
                  <InputLabel>Support Worker</InputLabel>
                  <Select
                    label="Support Worker"
                    {...getFieldProps('support_worder')}
                    value={values.support_worder || ''}
                    onChange={(e) => {
                      const selectedSupportWorker = supportWorkers.find(
                        (sw) => sw.name === e.target.value
                      );
                      setFieldValue('support_worder', e.target.value);
                      setFieldValue('supportworkers_email', selectedSupportWorker?.email || '');
                    }}
                  >
                    {supportWorkers.map((item: { id: string; name: string }) => (
                      <MenuItem key={item.id} value={item.name}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.support_worder && errors.support_worder && (
                    <FormHelperText error>{errors.support_worder as string}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  error={Boolean(touched.support_item_number && errors.support_item_number)}
                >
                  <InputLabel>Support Item Number</InputLabel>
                  <Select
                    label="Support Item Number"
                    {...getFieldProps('support_item_number')}
                    value={values.support_item_number || ''}
                    onChange={(e) => setFieldValue('support_item_number', e.target.value)}
                  >
                    {servicesList?.map((item: any) => (
                      <MenuItem key={item.CareNeed} value={item.CareNeed}>
                        {/* {item.CareNeed.split('|')[1] || item.CareNeed} */}
                        {item.CareNeed}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.support_item_number && errors.support_item_number && (
                    <FormHelperText error>{errors.support_item_number as string}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={values.allDay} {...getFieldProps('allDay')} />}
                  label="All day"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MobileDateTimePicker
                  label="Start date"
                  value={new Date(values.start)}
                  format="dd/MM/yyyy hh:mm a"
                  onChange={(date) => setFieldValue('start', date)}
                  slotProps={{
                    textField: {
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                            <DateRangeIcon />
                          </InputAdornment>
                        )
                      }
                    }
                  }}
                />
                {touched.start && errors.start && (
                  <FormHelperText error>{errors.start as string}</FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <MobileDateTimePicker
                  label="End date"
                  value={new Date(values.end)}
                  format="dd/MM/yyyy hh:mm a"
                  onChange={(date) => setFieldValue('end', date)}
                  slotProps={{
                    textField: {
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                            <DateRangeIcon />
                          </InputAdornment>
                        )
                      }
                    }
                  }}
                />
                {touched.end && errors.end && (
                  <FormHelperText error>{errors.end as string}</FormHelperText>
                )}
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Background Color</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl>
                      <RadioGroup
                        row
                        aria-label="color"
                        {...getFieldProps('color')}
                        onChange={(e) => setFieldValue('color', e.target.value)}
                        name="color-radio-buttons-group"
                        sx={{ '& .MuiFormControlLabel-root': { mr: 0 } }}
                      >
                        {backgroundColor.map((item, index) => (
                          <ColorPalette
                            key={index}
                            value={item.value}
                            color={item.color}
                            label={item.label!}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Text Color</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        aria-label="textColor"
                        {...getFieldProps('textColor')}
                        onChange={(e) => setFieldValue('textColor', e.target.value)}
                        name="text-color-radio-buttons-group"
                        sx={{ '& .MuiFormControlLabel-root': { mr: 0 } }}
                      >
                        <FormControlLabel
                          value=""
                          control={<Radio color="default" />}
                          label="Default"
                          sx={{ pr: 1 }}
                        />
                        {textColor.map((item, index) => (
                          <ColorPalette
                            key={index}
                            value={item.value}
                            color={item.color}
                            label={item.label!}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                {!isCreating && (
                  <Tooltip title="Delete Event">
                    <IconButton
                      onClick={() => {
                        console.log(event);
                        const selectedManager = managersList.find((m) => m.name === event.manager);
                        const selectedSupportWorker = supportWorkers.find(
                          (sw) => sw.name === event.support_worder
                        );
                        console.log(selectedManager, selectedSupportWorker);

                        handleDelete(
                          event?.id,
                          selectedManager?.email || '',
                          selectedSupportWorker?.email || ''
                        );
                      }}
                      size="large"
                    >
                      <DeleteIcon color="error" />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>
              <Grid item>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button type="button" variant="outlined" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || isClientsLoading}
                  >
                    {event ? 'Edit' : 'Add'}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </DialogActions>
        </Form>
      </LocalizationProvider>
    </FormikProvider>
  );
};

export default AddEventForm;
